'use client'

import { ReactNode, useMemo, useCallback } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import { FolderTreeIcon, ImageOff } from 'lucide-react'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { useCategoriesTree } from '@/hooks'
import { getResizedPictureUrl } from '@/lib/utils'
import { CategoryType } from '@/types/category'
import { useMetaStore } from '@/store'

type CategoryComboboxCommonProps = {
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: boolean
  clearable?: boolean
  excludeId?: string // filter out a category and all its descendants (e.g., current category when selecting parent)
  icon?: ReactNode
  id?: string
}

export type CategoryComboboxProps = CategoryComboboxCommonProps & {
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  multiple?: boolean
}

export function CategoryCombobox({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  error: hasError = false,
  clearable = true,
  excludeId,
  icon,
  multiple,
  id
}: CategoryComboboxProps) {
  const t = useTranslations()
  const locale = useLocale()
  const { metaState } = useMetaStore()
  const { data: categoriesTree, isLoading } = useCategoriesTree()

  const locales = useMemo(() => metaState.response?.locales || [], [metaState.response?.locales])

  const flattenCategories = useCallback(
    (
      categories: CategoryType[] = [],
      level = 0,
      result: { id: string; name: string; level: number; picturePath?: string }[] = []
    ) => {
      categories.forEach(category => {
        let name: string = ''

        const currentLocaleObj = locales.find((l: any) => l.fullCode === locale || l.code === locale)
        if (currentLocaleObj) {
          name = category.translations[currentLocaleObj.fullCode]?.name || ''
        }

        if (!name) {
          const firstTranslation = Object.values(category.translations)[0] as any
          name = (firstTranslation?.name as string) || category.id
        }

        result.push({
          id: category.id,
          name,
          level,
          picturePath: category.picture?.path
        })

        if (category.children && category.children.length > 0) {
          flattenCategories(category.children, level + 1, result)
        }
      })
      return result
    },
    [locale, locales]
  )

  const filterOutCategoryAndDescendants = useCallback(
    (categories: CategoryType[], categoryIdToFilter: string): CategoryType[] => {
      return categories
        .filter(cat => cat.id !== categoryIdToFilter)
        .map(cat => {
          if (cat.children && cat.children.length > 0) {
            return {
              ...cat,
              children: filterOutCategoryAndDescendants(cat.children, categoryIdToFilter)
            }
          }
          return cat
        })
    },
    []
  )

  const options: ComboboxOption[] = useMemo(() => {
    if (!categoriesTree) {
      return []
    }

    const filteredTree = excludeId ? filterOutCategoryAndDescendants(categoriesTree, excludeId) : categoriesTree

    const flattened = flattenCategories(filteredTree)

    return flattened.map(opt => ({
      label: opt.level > 0 ? `↳ ${opt.name}` : opt.name,
      value: opt.id,
      searchValue: opt.name,
      level: opt.level,
      icon:
        opt.level === 0 ? (
          opt.picturePath ? (
            <Image
              src={getResizedPictureUrl(opt.picturePath, 16, 16)}
              alt={opt.name}
              width={16}
              height={16}
              className="h-4 w-4"
            />
          ) : (
            <ImageOff className="text-muted-foreground h-4 w-4" />
          )
        ) : undefined
    }))
  }, [categoriesTree, excludeId, filterOutCategoryAndDescendants, flattenCategories])

  return (
    <Combobox
      id={id}
      options={options}
      value={value as any}
      onValueChange={newValue => {
        if (multiple) {
          if (newValue && typeof newValue === 'object' && 'length' in newValue) {
            ;(onValueChange as ((value: string[]) => void) | undefined)?.(newValue as string[])
          }
        } else {
          if (typeof newValue === 'string') {
            ;(onValueChange as ((value: string) => void) | undefined)?.(newValue)
          }
        }
      }}
      placeholder={placeholder || t('select')}
      searchPlaceholder={t('search')}
      emptyText={isLoading ? t('loading') : t('not_found')}
      disabled={disabled || isLoading}
      icon={icon || <FolderTreeIcon className="h-4 w-4" />}
      clearable={clearable}
      error={hasError}
      className={className}
      multiple={!!multiple}
    />
  )
}
